import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";
import { attT } from "@/lib/attendanceI18n";

const ShiftRow = ({ label, val, onChange, theme, disabled }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 110px 110px", gap: 8, alignItems: "center", marginBottom: 8 }}>
    <div style={{ fontSize: 13, color: theme.text }}>{label}</div>
    <input type="time" disabled={disabled} value={val?.start || "08:00"} onChange={(e) => onChange({ ...val, start: e.target.value })} style={inputStyle(theme)} />
    <input type="time" disabled={disabled} value={val?.end || "16:00"} onChange={(e) => onChange({ ...val, end: e.target.value })} style={inputStyle(theme)} />
  </div>
);

const inputStyle = (theme) => ({
  background: theme.surface, color: theme.text, border: `1px solid ${theme.border}`,
  borderRadius: 8, padding: "6px 8px", fontSize: 13, fontFamily: "inherit",
});

export default function WorkRulesTab({ canEdit }) {
  const { theme, lang } = useI18n();
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/attendance/work-rules");
      setR(data);
    } catch (e) { setErr(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!r) return <div style={{ padding: 40, color: theme.textMuted }}>{attT(lang, "loading")}</div>;

  const save = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      await api.put("/admin/attendance/work-rules", r);
      setOk(attT(lang, "saved_ok"));
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="attendance-rules-tab" style={{ maxWidth: 720 }}>
      {err && <div style={{ background: theme.errorBg, color: theme.errorText, padding: 10, borderRadius: 8, marginBottom: 10 }}>{err}</div>}
      {ok && <div style={{ background: "rgba(48,164,108,0.15)", color: "#30a46c", padding: 10, borderRadius: 8, marginBottom: 10 }}>{ok}</div>}

      <Section title={attT(lang, "rules.section.general")} theme={theme}>
        <Toggle label={attT(lang, "rules.friday_off")} checked={r.friday_off} onChange={(v) => setR({ ...r, friday_off: v })} theme={theme} disabled={!canEdit} testId="rule-friday-off" />
        <Toggle label={attT(lang, "rules.sat_half")} checked={r.saturday_half_day} onChange={(v) => setR({ ...r, saturday_half_day: v })} theme={theme} disabled={!canEdit} testId="rule-sat-half" />
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
          <label style={{ fontSize: 13 }}>{attT(lang, "rules.late_tolerance")}</label>
          <input type="number" min={0} max={120} disabled={!canEdit} value={r.late_tolerance_minutes} onChange={(e) => setR({ ...r, late_tolerance_minutes: parseInt(e.target.value || 0, 10) })} style={{ ...inputStyle(theme), width: 100 }} data-testid="rule-late-tolerance" />
        </div>
      </Section>

      <Section title={attT(lang, "rules.section.normal")} theme={theme}>
        <ShiftRow label={attT(lang, "rules.morning")} val={r.morning_shift} onChange={(v) => setR({ ...r, morning_shift: v })} theme={theme} disabled={!canEdit} />
        <ShiftRow label={attT(lang, "rules.evening")} val={r.evening_shift} onChange={(v) => setR({ ...r, evening_shift: v })} theme={theme} disabled={!canEdit} />
        <ShiftRow label={attT(lang, "rules.saturday")} val={r.saturday_shift} onChange={(v) => setR({ ...r, saturday_shift: v })} theme={theme} disabled={!canEdit} />
      </Section>

      <Section title={attT(lang, "rules.section.ramadan")} theme={theme}>
        <Toggle label={attT(lang, "rules.ramadan_active")} checked={r.ramadan_active} onChange={(v) => setR({ ...r, ramadan_active: v })} theme={theme} disabled={!canEdit} testId="rule-ramadan-active" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
          <Field label={attT(lang, "rules.ramadan_from")} theme={theme}><input type="date" disabled={!canEdit || !r.ramadan_active} value={r.ramadan_from || ""} onChange={(e) => setR({ ...r, ramadan_from: e.target.value })} style={inputStyle(theme)} data-testid="rule-ramadan-from" /></Field>
          <Field label={attT(lang, "rules.ramadan_to")} theme={theme}><input type="date" disabled={!canEdit || !r.ramadan_active} value={r.ramadan_to || ""} onChange={(e) => setR({ ...r, ramadan_to: e.target.value })} style={inputStyle(theme)} data-testid="rule-ramadan-to" /></Field>
        </div>
        <div style={{ opacity: r.ramadan_active ? 1 : 0.5, marginTop: 10 }}>
          <ShiftRow label={attT(lang, "rules.ramadan_morning")} val={r.ramadan_morning_shift} onChange={(v) => setR({ ...r, ramadan_morning_shift: v })} theme={theme} disabled={!canEdit || !r.ramadan_active} />
          <ShiftRow label={attT(lang, "rules.ramadan_evening")} val={r.ramadan_evening_shift} onChange={(v) => setR({ ...r, ramadan_evening_shift: v })} theme={theme} disabled={!canEdit || !r.ramadan_active} />
          <ShiftRow label={attT(lang, "rules.ramadan_saturday")} val={r.ramadan_saturday_shift} onChange={(v) => setR({ ...r, ramadan_saturday_shift: v })} theme={theme} disabled={!canEdit || !r.ramadan_active} />
        </div>
      </Section>

      {canEdit && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={save} disabled={busy} style={{ background: theme.gold, color: "#000", border: "none", padding: "10px 22px", borderRadius: 10, cursor: "pointer", fontWeight: 800 }} data-testid="rule-save">
            {busy ? attT(lang, "saving") : attT(lang, "rules.save")}
          </button>
        </div>
      )}
    </div>
  );
}

function Section({ title, children, theme }) {
  return (
    <section style={{ background: theme.surfaceCard, border: `1px solid ${theme.borderAccent}`, borderRadius: 12, padding: 16, marginBottom: 14 }}>
      <h3 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 800, color: theme.gold }}>{title}</h3>
      {children}
    </section>
  );
}

function Toggle({ label, checked, onChange, disabled, testId }) {
  return (
    <label style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 0", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.7 : 1 }}>
      <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
      <span style={{ fontSize: 13 }}>{label}</span>
    </label>
  );
}

function Field({ label, theme, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, color: theme.textMuted }}>{label}</label>
      {children}
    </div>
  );
}
